import { memo } from 'react';
import { Eye, Database, FolderOpen, Layers, Mail, LayoutDashboard, Coins, Shield, LogOut } from 'lucide-react';
import { cn } from '@/utils';
import { useAuth } from '@/contexts';
import type { TabType } from '@/types';

interface ApolloCredits {
  available: number;
  cached: number;
  oldestCacheAge?: string; // e.g., "5d" or "23h"
}

interface SidebarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  visitorCount: number;
  leadCount: number;
  listCount: number;
  contentCount: number;
  campaignCount?: number;
  contactCount?: number;
  apolloCredits?: ApolloCredits;
}

interface MenuItem {
  id: TabType;
  label: string;
  description: string;
  icon: React.ElementType;
  hideCount?: boolean;
}

const dataSourceItems: MenuItem[] = [
  { id: 'visitors', label: 'Website Visitors', description: 'Import & track visitors', icon: Eye },
  { id: 'leads', label: 'CRM Leads', description: 'Pipeline & enrichment', icon: Database },
  { id: 'content', label: 'Assets Library', description: 'Case studies & collateral', icon: FolderOpen },
];

const listsItems: MenuItem[] = [
  { id: 'lists', label: 'Lead Lists', description: 'Segment & sync to Monday', icon: Layers },
  { id: 'campaigns', label: 'Campaigns', description: 'Sequences & social analytics', icon: Mail },
];

export const Sidebar = memo(function Sidebar({
  activeTab,
  onTabChange,
  visitorCount,
  leadCount,
  listCount,
  contentCount,
  campaignCount = 2,
  contactCount = 2,
  apolloCredits,
}: SidebarProps) {
  const { user: authUser, isAdmin, logout } = useAuth();

  const getCounts = (id: TabType) => {
    switch (id) {
      case 'visitors':
        return visitorCount;
      case 'leads':
        return leadCount;
      case 'lists':
        return listCount;
      case 'content':
        return contentCount;
      case 'campaigns':
        return campaignCount;
      case 'contacts':
        return contactCount;
      default:
        return 0;
    }
  };

  const renderMenuItem = (item: MenuItem) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    const count = getCounts(item.id);

    return (
      <button
        key={item.id}
        onClick={() => onTabChange(item.id)}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
          isActive
            ? 'bg-zinc-800/80 text-white'
            : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
        )}
      >
        {/* Brand accent indicator */}
        {isActive && (
          <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-[#F78C4E]" />
        )}
        <div className={cn(
          'flex h-8 w-8 items-center justify-center rounded-lg',
          isActive ? 'bg-zinc-700' : 'bg-zinc-800/50'
        )}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{item.label}</p>
          <p className="text-xs text-zinc-500 truncate">{item.description}</p>
        </div>
        {!item.hideCount && (
          <span className={cn(
            'rounded-md px-2 py-0.5 text-xs font-medium',
            isActive ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-500'
          )}>
            {count}
          </span>
        )}
      </button>
    );
  };

  const isDashboardActive = activeTab === 'dashboard';

  return (
    <aside className="fixed left-0 top-0 z-40 flex h-screen w-60 flex-col border-r border-zinc-800 bg-zinc-900" role="navigation" aria-label="Main navigation">
      {/* Logo */}
      <div className="flex items-center gap-3 border-b border-zinc-800 px-4 py-4">
        <img
          src="/protagona-icon.svg"
          alt="Protagona"
          className="h-9 w-9"
        />
        <div>
          <h1 className="font-semibold text-white text-sm leading-tight">Protagona</h1>
          <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">Marketing Intelligence Hub</p>
        </div>
      </div>

      {/* Dashboard Link */}
      <div className="px-3 py-2">
        <button
          onClick={() => onTabChange('dashboard')}
          className={cn(
            'relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
            isDashboardActive
              ? 'bg-zinc-800/80 text-white'
              : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
          )}
        >
          {isDashboardActive && (
            <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-[#F78C4E]" />
          )}
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            isDashboardActive ? 'bg-zinc-700' : 'bg-zinc-800/50'
          )}>
            <LayoutDashboard className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">Dashboard</p>
            <p className="text-xs text-zinc-500 truncate">Overview & insights</p>
          </div>
        </button>
      </div>

      {/* Data Sources */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Data Sources
        </p>
        <div className="space-y-1">
          {dataSourceItems.map(renderMenuItem)}
        </div>

        <p className="mb-2 mt-6 px-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Lists & Campaigns
        </p>
        <div className="space-y-1">
          {listsItems.map(renderMenuItem)}
        </div>

        {/* Admin */}
        {isAdmin && (
          <>
            <p className="mb-2 mt-6 px-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              Administration
            </p>
            <div className="space-y-1">
              {renderMenuItem({ id: 'admin', label: 'Admin', description: 'Users & settings', icon: Shield, hideCount: true })}
            </div>
          </>
        )}
      </nav>

      {/* Quick Stats */}
      <div className="border-t border-zinc-800 p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Quick Stats
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-zinc-800/50 p-2">
            <p className="text-lg font-semibold text-white">{visitorCount}</p>
            <p className="text-[10px] text-zinc-500">Visitors</p>
          </div>
          <div className="rounded-lg bg-zinc-800/50 p-2">
            <p className="text-lg font-semibold text-white">{leadCount}</p>
            <p className="text-[10px] text-zinc-500">Leads</p>
          </div>
        </div>
      </div>

      {/* Apollo Credits */}
      {apolloCredits && (
        <div className="border-t border-zinc-800 p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Apollo Credits
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg bg-zinc-800/50 px-3 py-2">
              <span className="flex items-center gap-2 text-xs text-zinc-400">
                <Coins className="h-3.5 w-3.5 text-[#F78C4E]" />
                Available
              </span>
              <span className="text-sm font-medium text-white">{apolloCredits.available}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-zinc-800/50 px-3 py-2">
              <span className="flex items-center gap-2 text-xs text-zinc-400">
                <Database className="h-3.5 w-3.5 text-emerald-400" />
                Cached
              </span>
              <div className="text-right">
                <span className="text-sm font-medium text-emerald-400">{apolloCredits.cached}</span>
                {apolloCredits.oldestCacheAge && (
                  <p className="text-[9px] text-zinc-600">oldest: {apolloCredits.oldestCacheAge}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Info + Logout */}
      {authUser && (
        <div className="border-t border-zinc-800 p-3">
          <div className="flex items-center gap-2.5">
            {authUser.picture ? (
              <img src={authUser.picture} alt="" className="h-8 w-8 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-800 text-xs font-medium text-zinc-400">
                {authUser.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-xs font-medium text-white">{authUser.name}</p>
              <p className="truncate text-[10px] text-zinc-500">{authUser.role}</p>
            </div>
            <button
              onClick={logout}
              className="rounded-lg p-1.5 text-zinc-600 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
});
